import { sortBy, uniqBy } from "lodash";
import React, { useMemo } from "react";
import styled from "styled-components";

import { exists } from "@examspark/common";
import { useQueriesGetPerformanceTasks } from "@examspark/common-app";
import {
  CardAction,
  CardLink,
} from "@examspark/common-ui/dist/components/containers/LinkCard";
import { IconAssessmentLine } from "@examspark/common-ui/dist/components/icons";
import { Maybe, ParsedTCMPCUnit } from "@examspark/types";

import SidebarBox, { EmptySidebarBox } from "../common/SidebarBox";

import PinToPlannerButton from "./PinToPlannerButton";
import { SidebarLinkItem } from "./sidebar/BasicSidebarListItem";

const PerformanceTaskList = styled.div`
  box-shadow: 0 0 0 1px #ddd;
  border-radius: 0px 0px 5px 5px;
  background-color: white;
`;

const ClassroomSidebarAssessments = (props: {
  unit?: Maybe<ParsedTCMPCUnit>;
}) => {
  const performanceAssessmentsData = useMemo(
    () =>
      uniqBy(
        props.unit?.data?.subUnits?.flatMap((su) =>
          su.table.data.flatMap((r) => r.columns.assessments.data),
        ) ?? [],
        (d) => d.id,
      ),
    [props.unit?.data?.subUnits],
  );

  const performanceAssessmentsQueries = useQueriesGetPerformanceTasks(
    performanceAssessmentsData,
  );

  const performanceAssessmentsList = useMemo(
    () =>
      sortBy(
        performanceAssessmentsQueries.map((q) => q.data).filter(exists),
        (a) => a.name,
      ),
    [performanceAssessmentsQueries],
  );

  return (
    <>
      <SidebarBox title="Performance Assessments">
        {performanceAssessmentsList.length === 0 ? (
          <EmptySidebarBox>
            <IconAssessmentLine />
            <div>
              There are no Performance Assessments for this Unit / Module yet.
            </div>
          </EmptySidebarBox>
        ) : (
          <PerformanceTaskList>
            {performanceAssessmentsList.map((pa) => (
              <SidebarLinkItem key={pa.id}>
                <svg role="img" viewBox="0 0 24 24" width="24">
                  <g id="Icon">
                    <path d="m12 9.25c2.622 0 4.75 2.128 4.75 4.75s-2.128 4.75-4.75 4.75-4.75-2.128-4.75-4.75 2.128-4.75 4.75-4.75zm0 1.5c-1.794 0-3.25 1.456-3.25 3.25s1.456 3.25 3.25 3.25 3.25-1.456 3.25-3.25-1.456-3.25-3.25-3.25z"></path>
                    <path d="m9.67 14.53c-.293-.292-.293-.768 0-1.06.292-.293.768-.293 1.06 0l.67.669 1.87-1.869c.292-.293.768-.293 1.06 0 .293.292.293.768 0 1.06l-2.4 2.4c-.293.293-.767.293-1.06 0z"></path>
                    <path d="m8 3.25c.414 0 .75.336.75.75s-.336.75-.75.75h-2c-.332 0-.649.132-.884.366-.234.235-.366.552-.366.884v14c0 .332.132.649.366.884.235.234.552.366.884.366h12c.332 0 .649-.132.884-.366.234-.235.366-.552.366-.884v-14c0-.332-.132-.649-.366-.884-.235-.234-.552-.366-.884-.366h-2c-.414 0-.75-.336-.75-.75s.336-.75.75-.75h2c.729 0 1.429.29 1.945.805.515.516.805 1.216.805 1.945v14c0 .729-.29 1.429-.805 1.945-.516.515-1.216.805-1.945.805h-12c-.729 0-1.429-.29-1.945-.805-.515-.516-.805-1.216-.805-1.945v-14c0-.729.29-1.429.805-1.945.516-.515 1.216-.805 1.945-.805z"></path>
                    <path d="m16.75 3v2c0 .967-.784 1.75-1.75 1.75h-6c-.966 0-1.75-.783-1.75-1.75v-2c0-.967.784-1.75 1.75-1.75h6c.966 0 1.75.783 1.75 1.75zm-1.5 0c0-.138-.112-.25-.25-.25h-6c-.138 0-.25.112-.25.25v2c0 .138.112.25.25.25h6c.138 0 .25-.112.25-.25z"></path>
                  </g>
                </svg>
                <CardLink to={`/assessment/${pa.id}`}>{pa.name}</CardLink>
                <CardAction>
                  <PinToPlannerButton resource={pa} />
                </CardAction>
              </SidebarLinkItem>
            ))}
          </PerformanceTaskList>
        )}
      </SidebarBox>
    </>
  );
};

export default ClassroomSidebarAssessments;
